import { AfterContentInit, Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { RestService } from '../../../shared/service/rest.service';
import { MyProfileModel, NewRegistrationModel, RegisterAccountModel } from '../../../shared/model/UserProfile';
import { AuthServiceCustom } from 'src/shared/service/auth.service';
import { NgxUiLoaderService } from 'ngx-ui-loader'; // Import NgxUiLoaderService
import { AbstractDataProvider } from '../../AbstractDataProvider';
import { SecurityQuestionAnswer } from '../../security/QueryStringParam';
import { DictionaryPipe } from '../../../shared/pipes/dictionary.pipe';
import { StepperComponent } from '../stepper/stepper.component';
import { finalize } from 'rxjs/operators';
import { VerifyAccountByPinModel, VerifyAccountByTaxAndZip } from '../../../shared/model/interfaces';
import { HttpClient } from '@angular/common/http';
import {getErrorMessageText} from '../../helper';

export const EmailRegex = new RegExp('^(([^<>()\\[\\]\\\\.,;:\\s@"]+(\\.[^<>()\\[\\]\\\\.,;:\\s@"]+)*)|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$');
const PasswordRegex = new RegExp('^(?=\\D*\\d)(?=.*?[a-zA-Z]).*[\\W_].*$');

@Component({
    selector: 'app-investor-registration',
    templateUrl: './investor-registration.component.html',
    styleUrls: ['./investor-registration.component.scss'],
    providers: [DictionaryPipe],
    standalone: false
})
export class InvestorRegistrationComponent implements OnInit, AfterContentInit {

  @ViewChild('stepper') stepper: StepperComponent;

  userProfile: NewRegistrationModel = new NewRegistrationModel();

  display = false;
  displayConfirmationPopup = false;
  alertMessage: string;
  clientCode: string;
  isUserIdValid = false;

  steps: {title: string; id: string}[] = [];
  activeStep = 0;
  skipAdditionalVerification: boolean;

  securityQuestionAnswer: SecurityQuestionAnswer = new SecurityQuestionAnswer();
  questionList = [{SecQuestionText: 'dd', SecQuestionCode: 'CD1'}];
  isRegistrationCompleted = false;
  logoImageUrl: string;
  bgImageUrl: string;
  showPin: boolean;
  showTaxId: boolean;
  showPassword: boolean;
  showPasswordConfirmation: boolean;
  private saveTimeout: number;

  constructor(
    public rest: RestService,
    private authService: AuthServiceCustom,
    private abstractDataProvider: AbstractDataProvider,
    private dictionaryPipe: DictionaryPipe,
    private http: HttpClient,
    private ngxService: NgxUiLoaderService) {

    this.steps = [
      {title: 'REGISTER_NEW_ACCOUNT', id: 'REGISTER_NEW_ACCOUNT'},
      {title: 'ACCOUNT_VERIFICATION', id: 'ACCOUNT_VERIFICATION'},
      // {title: 'CHALLENGE_QUESTIONS', id: 'CHALLENGE_QUESTIONS'},
      {title: 'USER_PROFILE', id: 'USER_PROFILE'},
    ];
  }

  @HostListener('window:keyup', ['$event'])
  onEnterPress(event: KeyboardEvent): void {
    if (event.keyCode !== 13) { // enter
      return;
    }
    // TODO: fix this code
    // if (this.ngxService.getLoaders().master.foreground.default) {
    //   return;
    // }
    if (this.display || this.displayConfirmationPopup) {
      return;
    }
    if (this.activeStep !== this.steps.length - 1) {
      this.continue();
    } else {
      this.tryScheduleSave();
    }
  }

  ngOnInit() {
    this.clientCode = this.authService.clientCode;
    this.getListOfSecurityQuestions();
    this.bgImageUrl = this.authService.clientBackgroundImageUrl;
  }

  ngAfterContentInit() {
    this.clientCode = this.authService.clientCode;
    this.logoImageUrl = this.authService.clientLogoUrl;
    this.authService.accessTokenRemovedSubj.next(true);
  }

  getListOfSecurityQuestions(): void {
    const url = 'api/Configuration/GetSecurityQuestionList';
    this.abstractDataProvider.getData(url).subscribe(
      (posts) => {
        this.questionList = posts.SecurityQuestions;
      },
      (error) => {
        this.display = true;
        this.alertMessage =  getErrorMessageText(error);
      }
    );
  }

  validateUserName(): void {
    if (!this.userProfile.UserId || !this.userProfile.AccountNumber) {
      return;
    }

    this.userProfile.UserId = this.userProfile.UserId.trim();
    const saveAfterValidation = !!this.saveTimeout;
    clearTimeout(this.saveTimeout);
    this.saveTimeout = null;
    this.ngxService.start();
    const dto = {
      RecordType: 'I',
      UserId: this.userProfile.UserId,
      AccountNumber: this.userProfile.AccountNumber && parseInt(this.userProfile.AccountNumber, 10),
      Pin: this.userProfile.PIN,
      TaxId: this.userProfile.TaxId,
      ZipCode: this.userProfile.Zip
    };
    this.abstractDataProvider.postData(dto, 'api/Accounts/VerifyUserIdAvailable').pipe(finalize(() => this.ngxService.stop())).subscribe((data: {Available: boolean}) => {
      if (!data.Available) {
        this.display = true;
        this.alertMessage = 'This Username is already taken.';
        this.isUserIdValid = false;
      } else if (saveAfterValidation) {
        this.isUserIdValid = true;
        this.save();
      } else {
        this.isUserIdValid = true;
      }
    }, error => {
        this.isUserIdValid = false;
      this.display = true;
      this.alertMessage =  getErrorMessageText(error);
    });
  }

  save(): void {

    const errors = [];
    if (!this.userProfile.AccountNumber) {
      errors.push(this.dictionaryPipe.transform('ENTER_INVESTOR_ID'));
    }

    if (this.userProfile.AccountNumber && !parseInt(this.userProfile.AccountNumber, 10)) {
      errors.push(this.dictionaryPipe.transform('ENTER_INVESTOR_ID'));
    }

    if (!this.skipAdditionalVerification) {
      if (!this.userProfile.Zip) {
        errors.push('Zip Code is required.');
      }

      if (!this.userProfile.TaxId) {
        errors.push('Tax Id is required.');
      }
    }

    // if (!this.userProfile.UserId) {
    //   errors.push('Username is required');
    // }

    // if (this.userProfile.UserId && !this.isUserIdValid) {
    //   errors.push('Username must be unique');
    // }

    if (!this.userProfile.EmailAddress) {
      errors.push('Email Address is required');
    }

    if (!this.userProfile.ConfirmEmailAddress) {
      errors.push('Confirm Email Address is required');
    }

    if (this.userProfile.ConfirmEmailAddress && this.userProfile.EmailAddress && this.userProfile.ConfirmEmailAddress !== this.userProfile.EmailAddress) {
      errors.push('Confirm Email Address does not match');
    }

    if (this.userProfile.EmailAddress && !this.userProfile.EmailAddress.match(EmailRegex)) {
      errors.push('Invalid Email Address');
    }

    this.userProfile.UserId = this.userProfile.EmailAddress;

    // if (!this.userProfile.Password) {
    //   errors.push('Password is required');
    // }

    if (!this.userProfile.TermsAndConditionAgreed) {
      errors.push('Please accept Terms and Condition');
    }

    // if (this.userProfile.Password) {
    //   const minLength = 8; // TODO: move outside the method
    //   if (this.userProfile.Password.length < minLength) {
    //     errors.push(this.dictionaryPipe.transform('AT_LEAST_CHARACTERS', minLength));
    //   }
    //   if (!PasswordRegex.test(this.userProfile.Password)) {
    //     errors.push(this.dictionaryPipe.transform('ALPHA_NUMERIC_SPECIAL'));
    //   }
    // }

    // const validatedObj = this.authService.validateSecurityQuestionAnswer(this.securityQuestionAnswer);
    // if (!validatedObj.isValid) {
    //   errors.push(validatedObj.alertmessage);
    // }

    // if (!this.userProfile.ConfirmPassword) {
    //   errors.push('Confirm Password is required');
    // }

    // if (this.userProfile.ConfirmPassword && this.userProfile.Password && this.userProfile.ConfirmPassword !== this.userProfile.Password) {
    //   errors.push('Confirm Password does not match ');
    // }

    if (errors.length) {
      this.display = true;
      this.alertMessage = 'Please fix errors: <br>' + errors.join(', <br>');
      return;
    }


    const dto = {
      RecordType: 'I',
      AccountNumber: parseInt(this.userProfile.AccountNumber, 10),
      Pin: this.userProfile.PIN,
      TaxId: this.userProfile.TaxId,
      ZipCode: this.userProfile.Zip,
      UserName: this.userProfile.UserId.toUpperCase(),
      Password: this.userProfile.Password,
      EmailAddress: this.userProfile.EmailAddress,

      // SecQuestionText1: this.questionList.find(item => item.SecQuestionCode === this.securityQuestionAnswer.SecQuestionCode1).SecQuestionText,
      // SecQuestionCode1: this.securityQuestionAnswer.SecQuestionCode1,
      // SecAnswerText1: this.securityQuestionAnswer.SecAnswerText1,
      // SecQuestionText2: this.questionList.find(item => item.SecQuestionCode === this.securityQuestionAnswer.SecQuestionCode2).SecQuestionText,
      // SecQuestionCode2: this.securityQuestionAnswer.SecQuestionCode2,
      // SecAnswerText2: this.securityQuestionAnswer.SecAnswerText2,
      // SecQuestionText3: this.questionList.find(item => item.SecQuestionCode === this.securityQuestionAnswer.SecQuestionCode3).SecQuestionText,
      // SecQuestionCode3: this.securityQuestionAnswer.SecQuestionCode3,
      // SecAnswerText3: this.securityQuestionAnswer.SecAnswerText3,

      SendEmails: this.userProfile.ElectronicMailAgreed ? 'Y' : 'N'
    } as RegisterAccountModel;
    this.ngxService.start();

    this.abstractDataProvider.postData(dto, 'api/Accounts/RegisterAccount').pipe(finalize(() => this.ngxService.stop())).subscribe(() => {
      this.isRegistrationCompleted = true;
    }, error => {
      this.display = true;
      this.alertMessage =  getErrorMessageText(error, error.error);
    });
  }

  showTerms(): void {
    const url = `assets/TERMS.html`;
    this.http.get(url, {responseType: 'text'}).subscribe(data => {
      this.display = true;
      this.alertMessage = data;
    });
  }

  preventPaste(e: any): void {
    e.preventDefault();
  }

  continue(): void {
    if (this.steps[this.activeStep].id === 'REGISTER_NEW_ACCOUNT') {
      this.validatePinAndAccountNum();
      return;
    }
    if (this.steps[this.activeStep].id === 'ACCOUNT_VERIFICATION') {
      this.additionalVerificationCheck();
      return;
    }

    if (this.steps[this.activeStep].id === 'CHALLENGE_QUESTIONS') {

      const validatedObj = this.authService.validateSecurityQuestionAnswer(this.securityQuestionAnswer);
      if (!validatedObj.isValid) {
        this.display = true;
        this.alertMessage = validatedObj.alertmessage;
        return;
      }
      this.stepper.changeStep(this.activeStep + 1);

      return;
    }

  }

  submit() {
    if (!this.userProfile.PIN) {
      this.display = true;
      this.alertMessage = this.dictionaryPipe.transform('ENTER_PIN');
      return;
    }
  }

  cancel() {
    this.authService.navigateToLogin();
  }

  onClosePopup() {
    this.display = false;
  }

  onConfirm(ok: boolean): void {
    this.displayConfirmationPopup = false;

    if (ok === false) {
      this.stepper.changeStep(this.activeStep + 1);
    }
  }

  navigateToLogin(isDisplayed = false) {
    this.isRegistrationCompleted = isDisplayed;
    this.authService.navigateToLogin();
  }

  tryScheduleSave() {
    // TODO: fix this code
    // if (this.ngxService.getLoaders().master.foreground.default) {
    //   return;
    // }
    this.saveTimeout = window.setTimeout(() => {
      this.saveTimeout = null;
      this.save();
    });
  }


  private additionalVerificationCheck(): void {
    if (!this.userProfile.Zip) {
      this.display = true;
      this.alertMessage = 'Zip Code is required.';
      return;
    } else if (!this.userProfile.TaxId) {
      this.display = true;
      this.alertMessage = 'Tax Id  is required.';
      return;
    } else {
      // TODO: fix this code
      // if (this.ngxService.getLoaders().master.foreground.default) {
      //   return;
      // }
      this.ngxService.start();

      const dto = {
        AccountNumber: this.userProfile.AccountNumber,
        ZipCode: this.userProfile.Zip,
        TaxId: this.userProfile.TaxId,
      } as VerifyAccountByTaxAndZip;

      this.abstractDataProvider.postData(dto, 'api/Accounts/VerifyAccountByTaxAndZip')
        .pipe(finalize(() => this.ngxService.stop()))
        .subscribe((userProfile: {Data: MyProfileModel}) => {
          this.fillInputs(userProfile);
          this.stepper.changeStep(this.activeStep + 1);
        }, error => {
            if (error.status === 403) {
            this.display = true;
            this.alertMessage =  getErrorMessageText(error, 'User already registered.');

          } else {
            this.display = true;
            this.alertMessage = getErrorMessageText(error, error.error);
          }
        });
    }
  }

  private validatePinAndAccountNum(): void {
    // TODO: fix this code
    // if (this.ngxService.getLoaders().master.foreground.default) {
    //   return;
    // }
    this.skipAdditionalVerification = false;
    if (!this.userProfile.AccountNumber) {
      this.display = true;
      this.alertMessage = this.dictionaryPipe.transform('ENTER_INVESTOR_ID');
      return;
    }

    if (this.userProfile.AccountNumber && !parseInt(this.userProfile.AccountNumber, 10)) {
      this.display = true;
      this.alertMessage = this.dictionaryPipe.transform('INVESTOR_ID_NUMERIC');
      return;
    }


    if (this.userProfile.PIN) {
      this.ngxService.start();

      const dto = {
        AccountNumber: parseInt(this.userProfile.AccountNumber, 10),
        PIN: this.userProfile.PIN.toUpperCase(),
        RecordType: 'I'
      } as VerifyAccountByPinModel;

      this.abstractDataProvider.postData(dto, 'api/Accounts/VerifyAccountByPin')
        .pipe(finalize(() => this.ngxService.stop()))
        .subscribe((userProfile: {Data: MyProfileModel}) => {
          this.skipAdditionalVerification = Boolean(userProfile);
          const index = this.steps.findIndex(s => s.id === 'ACCOUNT_VERIFICATION');

          if (!userProfile) {
            this.displayConfirmationPopup = true;
            this.alertMessage = 'PIN or Investor ID is invalid. Please try again or continue with further verification.'; // TODO: move to dictionary
            if (index === -1) {
              this.steps.splice(1, 0, {title: 'ACCOUNT_VERIFICATION', id: 'ACCOUNT_VERIFICATION'});
            }
          } else {
            if (index > -1) {
              this.steps.splice(index, 1);
              this.stepper.changeStep(this.activeStep + 1);
              this.userProfile.Zip = '';
              this.userProfile.TaxId = '';
            }
            this.fillInputs(userProfile);

          }
        }, error => {
          if (error.status === 403) {
            this.display = true;
            this.alertMessage =  getErrorMessageText(error, 'User already registered.');

          } else {
            this.displayConfirmationPopup = true;
            this.alertMessage =  getErrorMessageText(error, 'PIN or Investor ID is invalid. Please try again or continue with further verification.');
          }
        });

    } else {
      this.skipAdditionalVerification = false;
      this.stepper.changeStep(this.activeStep + 1);
    }
  }

  private fillInputs(userProfile: {Data: MyProfileModel}): void {
    const sec = userProfile.Data.SecurityQuestions;
    this.securityQuestionAnswer.SecQuestionCode1 = sec.SecQuestionCode1;
    this.securityQuestionAnswer.SecQuestionCode2 = sec.SecQuestionCode2;
    this.securityQuestionAnswer.SecQuestionCode3 = sec.SecQuestionCode3;
    this.securityQuestionAnswer.SecAnswerText1 = sec.SecAnswerText1;
    this.securityQuestionAnswer.SecAnswerText2 = sec.SecAnswerText2;
    this.securityQuestionAnswer.SecAnswerText3 = sec.SecAnswerText3;

    this.userProfile.UserId = userProfile.Data.EmailAddress;
    this.userProfile.EmailAddress = userProfile.Data.EmailAddress;
    this.userProfile.ConfirmEmailAddress = userProfile.Data.EmailAddress;
    this.userProfile.ElectronicMailAgreed = userProfile.Data.IsElectronicMailSubscribed;
    this.userProfile.ElectronicMailAgreed = Boolean(userProfile.Data.IsElectronicMailSubscribed === 'Y');
  }

  toUpperCaseInput(event: KeyboardEvent) {
    const input = (event.target as HTMLInputElement);
    input.value = input.value && input.value.toString().toUpperCase();
  }
}
